"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import useResizeObserver from "@churchofjesuschrist/eden-resize-observer";

/**
 * Creates a throttled version of a function that limits the rate at which the function can be called.
 * @param {Function} fn - The function to be throttled.
 * @param {number} [time] - The time interval in milliseconds between function invocations.
 * @returns {Function} - The throttled function.
 */
const throttle = (fn, time = 50) => {
  let timer = null;
  function throttledFn(...args) {
    if (!timer) {
      timer = setTimeout(() => {
        fn(...args);
        timer = null;
      }, time);
    }
  }
  throttledFn.cancel = () => {
    clearTimeout(timer);
    timer = null;
  };
  return throttledFn;
};

/**
 * Utility to determine if the component has overflow. Uses a function as a child pattern to extend the props with additional information relative to overflow. See Details section for example and more information.
 * @param {object} props - The component props
 * @param {Function} props.children - Render prop which is called with wrapping element overflow data
 * @param {boolean} [props.startX] - Whether there is X overflow by default
 * @param {boolean} [props.startY] - Whether there is Y overflow by default
 * @param {boolean} [props.start] - Whether there is Start overflow by default
 * @param {boolean} [props.end] - Whether there is End overflow by default
 * @param {boolean} [props.left] - Whether there is Left overflow by default
 * @param {boolean} [props.right] - Whether there is Right overflow by default
 * @param {boolean} [props.top] - Whether there is Top overflow by default
 * @param {boolean} [props.bottom] - Whether there is Bottom overflow by default
 * @param {number} [props.fromStart] - Space from start content to container
 * @param {number} [props.fromEnd] - Space from end content to container
 * @param {number} [props.fromTop] - Space from top content to container
 * @param {number} [props.fromBottom] - Space from bottom content to container
 * @returns {React.ReactElement} The HasOverflow component
 */
const HasOverflow = ({
  children,
  startX = false,
  startY = false,
  start = false,
  end = false,
  left = false,
  right = false,
  top = false,
  bottom = false,
  fromStart = 0,
  fromEnd = 0,
  fromTop = 0,
  fromBottom = 0
}) => {
  const [overflowRef, overflowValues] = useHasOverflow(false, {
    startX: startX,
    startY: startY,
    start: start,
    end: end,
    left: left,
    right: right,
    top: top,
    bottom: bottom,
    fromStart: fromStart,
    fromEnd: fromEnd,
    fromTop: fromTop,
    fromBottom: fromBottom
  });
  return children({
    refHandler: overflowRef,
    ...overflowValues
  });
};
HasOverflow.displayName = "HasOverflow";
export default HasOverflow;
export const useHasOverflow = (update, defaultValues = {
  startX: false,
  startY: false,
  start: false,
  end: false,
  left: false,
  right: false,
  top: false,
  bottom: false,
  fromStart: 0,
  fromEnd: 0,
  fromTop: 0,
  fromBottom: 0
}) => {
  const [overflowValues, setOverflowValues] = useState({
    hasX: defaultValues.startX,
    hasY: defaultValues.startY,
    hasStart: defaultValues.start,
    hasEnd: defaultValues.end,
    hasTop: defaultValues.top,
    hasLeft: defaultValues.left,
    hasRight: defaultValues.right,
    hasBottom: defaultValues.bottom,
    fromStart: defaultValues.fromStart,
    fromEnd: defaultValues.fromEnd,
    fromTop: defaultValues.fromTop,
    fromBottom: defaultValues.fromBottom
  });
  const [direction, setDirection] = useState("");
  const [dimensions, setDimensions] = useState(0);
  const onResize = useCallback(([resizeEntry]) => {
    setDimensions(Math.round(resizeEntry.contentRect.width + resizeEntry.contentRect.height));
  }, []);
  const overflowRef = useRef();
  useResizeObserver(onResize, overflowRef);
  useEffect(() => {
    // Based on https://stackoverflow.com/a/24394376
    const getScrollLeft = () => {
      const {
        clientWidth,
        scrollWidth,
        scrollLeft
      } = overflowRef.current || {};

      // Perform the calculations only when direction is rtl to avoid messing up the ltr behavior
      if (direction !== "rtl") {
        return scrollLeft;
      } else {
        return scrollWidth - clientWidth + scrollLeft;
      }
    };
    const checkOverflow = throttle(() => {
      if (overflowRef.current) {
        const {
          clientHeight,
          scrollHeight,
          clientWidth,
          scrollWidth,
          scrollTop
        } = overflowRef.current;
        const scrollLeft = getScrollLeft();
        const left = scrollLeft > 1;
        const right = scrollWidth - clientWidth - scrollLeft > 1;
        setOverflowValues({
          hasX: clientWidth < scrollWidth,
          hasY: clientHeight < scrollHeight,
          hasStart: direction === "ltr" ? left : scrollLeft < scrollWidth - clientWidth,
          hasEnd: direction === "ltr" ? right : scrollLeft > 0,
          hasTop: scrollTop > 1,
          hasLeft: left,
          hasRight: right,
          hasBottom: scrollHeight - clientHeight - scrollTop > 1,
          fromStart: scrollLeft,
          fromEnd: scrollWidth - clientWidth - scrollLeft,
          fromTop: scrollTop,
          fromBottom: scrollHeight - clientHeight - scrollTop
        });
      }
    }, 125);
    checkOverflow();
    if (overflowRef.current) {
      setDirection(overflowRef.current.closest("[dir='rtl']") ? "rtl" : "ltr");
      overflowRef.current.addEventListener("scroll", checkOverflow);
    }
    const currentFirstChild = overflowRef.current;
    return () => {
      checkOverflow.cancel();
      if (currentFirstChild) {
        currentFirstChild.removeEventListener("scroll", checkOverflow);
      }
    };
  }, [direction, update, overflowRef, dimensions]);
  return [overflowRef, {
    ...overflowValues,
    direction
  }];
};